import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { conditionalArray } from '../../helpers/array';
import { useAppContext } from '../../pages/App/AppContextProvider';
import CreateMultiAutocomplete from '../CreateMultiAutocomplete';
import messages from './messages';
import MultiAutocompleteOption from './MultiAutocompleteOption';
import UsersForm from './UsersForm';

export const ADD_ALL_USERS_ID = 'addAllUsersId';

interface IUsersAutocompleteProps {
  areAllUsersAdded?: boolean;
  hasAddAllUsersOption?: boolean;
  hasFormItemKey: string;
  hasSelectedUsers: boolean;
  newItemId: string;
  newItemWithoutLoginId: string;
  optionsData: Array<{
    email?: string;
    firstName?: string;
    id: string;
    label: string;
    lastName: string;
  }>;
  scrollableTags?: boolean;
}

function UsersAutocomplete({
  areAllUsersAdded,
  hasAddAllUsersOption,
  hasFormItemKey,
  hasSelectedUsers,
  newItemId,
  newItemWithoutLoginId,
  optionsData,
  scrollableTags,
}: IUsersAutocompleteProps) {
  const { watch } = useFormContext();
  const { formatMessage } = useIntl();
  const { isUserLoggedAsOrySso } = useAppContext();

  const options = useMemo(
    () =>
      conditionalArray([
        !!hasAddAllUsersOption && {
          id: ADD_ALL_USERS_ID,
          label: formatMessage(messages.usersAutocompleteSelectAddAll),
        },
        !isUserLoggedAsOrySso && {
          id: newItemId,
          label: formatMessage(messages.usersAutocompleteNewItemLabel),
          tag: formatMessage(messages.usersAutocompleteNewItemShortLabel),
        },
        !isUserLoggedAsOrySso && {
          id: newItemWithoutLoginId,
          label: formatMessage(messages.usersAutocompleteNewItemWithoutLoginLabel),
          tag: formatMessage(messages.usersAutocompleteNewItemWithoutLoginShortLabel),
        },
        ...optionsData,
      ]),
    [formatMessage, hasAddAllUsersOption, isUserLoggedAsOrySso, newItemId, newItemWithoutLoginId, optionsData]
  );

  return (
    <CreateMultiAutocomplete
      addAllOptionsId={ADD_ALL_USERS_ID}
      areAllOptionsAdded={areAllUsersAdded}
      scrollableTags={scrollableTags}
      createForm={watch(hasFormItemKey) && <UsersForm />}
      createFormVisibilityKey={hasFormItemKey}
      customOption={(renderProps, option: { id: string }) => (
        <MultiAutocompleteOption
          key={option.id}
          addAllOptionsId={ADD_ALL_USERS_ID}
          hasAddAllOptions={hasAddAllUsersOption}
          newItemId={newItemId}
          newItemWithoutLoginId={newItemWithoutLoginId}
          option={option}
          optionLabelKey="label"
          renderProps={renderProps}
        />
      )}
      helperText={formatMessage(messages.usersAutocompleteHelperText)}
      label={formatMessage(messages.usersAutocompleteLabel)}
      name="users"
      newItemId={newItemId}
      newItemId2={newItemWithoutLoginId}
      optionLabelKey="label"
      options={options}
      placeholder={hasSelectedUsers ? '' : formatMessage(messages.usersAutocompletePlaceholder)}
    />
  );
}

export default UsersAutocomplete;
